import Phaser from 'phaser';
import {
  ACTIVE_SKILLS,
  ULT_SKILLS,
  SKILL_FX,
  GAME_WIDTH,
  GAME_HEIGHT,
  type ActiveSkillKey,
  type UltSkillKey,
} from '../config/GameConfig';
import type Player from '../entities/Player';
import type Enemy from '../entities/Enemy';
import type Effects from '../ui/Effects';
import type SoundManager from './SoundManager';

/**
 * SkillSystem —— 主动技能 + ULT 系统
 *
 * 职责：
 *   - 持有 3 个槽（A1, A2, U）的当前选择 + 冷却 / 蓄能状态
 *   - 触发主动技能：dash/pulse/timewarp 各自的实现
 *   - ULT 蓄能：MainScene 在击杀 / 受击 hook 里调 addCharge；满了允许 fire
 *
 * 不持有 Phaser 物理组：行为通过 hooks 拿到 enemyGroup / bossBulletGroup。
 * HUD 渲染由 [HUD] 拉数据（SkillSystem.snapshot()）。
 */

export interface SkillHooks {
  /** 拿到当前敌人组，用于 pulse / nuke 等 AOE */
  getEnemyGroup: () => Phaser.Physics.Arcade.Group;
  /** 拿到 Boss 子弹组，用于 nuke 清弹幕 */
  getBossBulletGroup: () => Phaser.Physics.Arcade.Group;
  /**
   * 走标准击杀流程（计分 / 金币 / 爆炸） —— 接 MainScene._onEnemyKilled
   * pulse / nuke 命中致死时调
   */
  onEnemyKilled: (enemy: Enemy) => void;
  /** 拿到当前游戏 paused 状态（暂停 / 死亡 / 升级面板期间所有技能都被锁） */
  isLocked: () => boolean;
}

interface ActiveState {
  key: ActiveSkillKey;
  /** 冷却结束的时间戳（performance.now()）；<= now 表示可用 */
  readyAt: number;
}

interface UltState {
  key: UltSkillKey;
  charge: number;       // 当前蓄能 [0, chargeMax]
  /** 释放后的"持续期"截止时间 —— 期间不能再放（也不蓄能），效果由 update() 维护 */
  activeUntil: number;
}

export default class SkillSystem {
  scene: Phaser.Scene;
  player: Player;
  effects: Effects;
  sound: SoundManager | null;
  hooks: SkillHooks;

  /** 主动 1 / 主动 2 / ULT 状态 */
  a1: ActiveState;
  a2: ActiveState;
  ult: UltState;

  /** timewarp 是否生效中（让 update() 知道要不要每帧维护） */
  private _timewarpUntil = 0;
  /** frenzy ULT 生效中（影响 fireRate / bulletCount / critChance） */
  private _frenzyUntil = 0;
  /** frenzy 期间需要恢复的玩家原值 */
  private _frenzySnapshot: { fireRate: number; bulletCount: number; critChance: number } | null = null;

  constructor(
    scene: Phaser.Scene,
    player: Player,
    effects: Effects,
    hooks: SkillHooks,
    a1Key: ActiveSkillKey = 'dash',
    a2Key: ActiveSkillKey = 'pulse',
    ultKey: UltSkillKey = 'nuke',
    sound: SoundManager | null = null,
  ) {
    this.scene = scene;
    this.player = player;
    this.effects = effects;
    this.hooks = hooks;
    this.sound = sound;

    this.a1 = { key: a1Key, readyAt: 0 };
    this.a2 = { key: a2Key, readyAt: 0 };
    this.ult = { key: ultKey, charge: 0, activeUntil: 0 };
  }

  // ─── 触发入口（HUD 按钮 / 键盘）─────────────────────────────────

  /** 尝试触发 A1（主动 1）。返回 true 表示成功放出 */
  fireA1(): boolean { return this._fireActive(this.a1); }
  fireA2(): boolean { return this._fireActive(this.a2); }
  fireUlt(): boolean {
    if (this.hooks.isLocked()) return false;
    if (this.ult.charge < this._ultCfg().chargeMax) return false;
    if (performance.now() < this.ult.activeUntil) return false; // 还在持续期
    this.ult.charge = 0;
    this._executeUlt(this.ult.key);
    return true;
  }

  private _fireActive(slot: ActiveState): boolean {
    if (this.hooks.isLocked()) return false;
    const now = performance.now();
    if (now < slot.readyAt) return false;
    slot.readyAt = now + ACTIVE_SKILLS[slot.key].cooldownMs;
    this._executeActive(slot.key);
    return true;
  }

  // ─── 蓄能（外部 hook） ─────────────────────────────────────────

  /** 击杀 +1 蓄能（所有 ULT 都吃这个） */
  onKill(): void {
    const cfg = this._ultCfg();
    if (this.ult.charge < cfg.chargeMax) {
      this.ult.charge = Math.min(cfg.chargeMax, this.ult.charge + 1);
    }
  }
  /** 受击 +N 蓄能（仅 frenzy 受这个） */
  onHurt(): void {
    if (this.ult.key !== 'frenzy') return;
    const cfg = this._ultCfg();
    this.ult.charge = Math.min(cfg.chargeMax, this.ult.charge + 5);
  }

  // ─── 主循环 ────────────────────────────────────────────────────

  update(_delta: number): void {
    const now = performance.now();
    // timewarp 期间每帧把 timeScale 强制写回目标值 ——
    // 避免 hit-pause（80ms 慢动作）80ms 后还原成 1 把 timewarp 抹掉
    if (this._timewarpUntil > 0) {
      if (now >= this._timewarpUntil) {
        this._timewarpUntil = 0;
        this.scene.physics.world.timeScale = 1;
      } else {
        // 强制写回（hit-pause 已经过期回到 1 时再次写回；hit-pause 还在跑就维持 20）
        // 这里的策略：timewarp 优先级更高，因为它是玩家主动技能；
        // hit-pause 仅用于受击瞬间，与"我在用时缓"是矛盾的（玩家用了时缓本来就不该被打）
        const targetTs = SKILL_FX.timewarp.worldTimeScale;
        if (this.scene.physics.world.timeScale !== targetTs) {
          this.scene.physics.world.timeScale = targetTs;
        }
      }
    }
    // frenzy 到期：还原玩家属性
    if (this._frenzyUntil > 0 && now >= this._frenzyUntil) {
      this._frenzyUntil = 0;
      if (this._frenzySnapshot) {
        this.player.shootingDelay = this._frenzySnapshot.fireRate;
        this.player.bulletCount = this._frenzySnapshot.bulletCount;
        this.player.critChance = this._frenzySnapshot.critChance;
        this._frenzySnapshot = null;
      }
    }
  }

  // ─── HUD 数据快照 ──────────────────────────────────────────────

  snapshot() {
    const now = performance.now();
    return {
      a1: this._activeSnap(this.a1, now),
      a2: this._activeSnap(this.a2, now),
      ult: this._ultSnap(now),
    };
  }
  private _activeSnap(slot: ActiveState, now: number) {
    const cfg = ACTIVE_SKILLS[slot.key];
    const leftMs = Math.max(0, slot.readyAt - now);
    const ratio = leftMs <= 0 ? 1 : 1 - leftMs / cfg.cooldownMs;
    return { key: slot.key, cfg, ratio, leftMs };
  }
  private _ultSnap(now: number) {
    const cfg = this._ultCfg();
    const isActive = now < this.ult.activeUntil;
    const ratio = Math.min(1, this.ult.charge / cfg.chargeMax);
    return { key: this.ult.key, cfg, ratio, charge: this.ult.charge, isActive };
  }

  // ─── 切换技能（MenuScene 选好后写入；MainScene create 时读 registry）

  setLoadout(a1: ActiveSkillKey, a2: ActiveSkillKey, ult: UltSkillKey): void {
    this.a1 = { key: a1, readyAt: 0 };
    this.a2 = { key: a2, readyAt: 0 };
    this.ult = { key: ult, charge: 0, activeUntil: 0 };
  }

  // ─── 各技能实现 ────────────────────────────────────────────────

  private _executeActive(key: ActiveSkillKey): void {
    if (key === 'dash') {
      // dash 复用 Player.tryDash —— 该方法已自管 CD（_dashReadyAt）
      // 但我们这里也已经 set readyAt 了；为了不双重 CD，dash 这里直接调用 player 的"硬闪"逻辑
      this._doDash();
    } else if (key === 'pulse') {
      this._doPulse();
    } else if (key === 'timewarp') {
      this._doTimewarp();
    }
  }

  private _executeUlt(key: UltSkillKey): void {
    if (key === 'nuke') this._doNuke();
    else if (key === 'frenzy') this._doFrenzy();
  }

  /** dash —— 跟 Player.tryDash 等价但不再读 _dashReadyAt（CD 由 SkillSystem 管） */
  private _doDash(): void {
    // 直接调 Player.tryDash 来复用残影 / 无敌 / 视觉，但需要绕开内部 CD
    // 简单做法：把内部 readyAt 清零让它必触发；触发后 SkillSystem 已写入自己的 CD
    (this.player as unknown as { _dashReadyAt: number })._dashReadyAt = 0;
    this.player.tryDash();
    this.sound?.shieldBreak();
    this.scene.cameras.main.shake(70, 0.004);
  }

  /** pulse —— 玩家位置 AOE，把范围内非 Boss 怪推开 + 造成固定伤害 */
  private _doPulse(): void {
    const fx = SKILL_FX.pulse;
    const x = this.player.x;
    const y = this.player.y;
    const r2 = fx.radius * fx.radius;

    // 玩家短暂无敌，避免被推开瞬间贴脸怪反伤
    this.player._invincibleUntil = Math.max(this.player._invincibleUntil, performance.now() + fx.invincibleMs);

    // 视觉：白色冲击波 + 屏抖
    this.effects.explosion(x, y, { big: true, color: 0xffd166, count: 30 });
    this.scene.cameras.main.shake(180, 0.014);

    // 物理：扫敌人
    this.hooks.getEnemyGroup().getChildren().forEach((e) => {
      const en = e as Enemy;
      if (!en.active || en._dead || !en.body) return;
      const dx = en.x - x;
      const dy = en.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 > r2) return;
      const d = Math.sqrt(d2) || 1;
      // Boss / mini Boss 不被推开（只吃伤），保留 Boss 战节奏
      if (!en.isBoss) {
        en.body.velocity.x = (dx / d) * fx.knockback;
        en.body.velocity.y = (dy / d) * fx.knockback;
      }
      const dead = en.takeDamage(fx.damage);
      if (dead) {
        en._dead = true;
        this.hooks.onEnemyKilled(en);
      }
    });

    // 屏内 Boss 弹幕在 pulse 范围内的也清掉（玩家自卫的合理预期）
    this.hooks.getBossBulletGroup().getChildren().forEach((p) => {
      const b = p as unknown as { active: boolean; x: number; y: number; destroy: () => void };
      if (!b.active) return;
      const dx = b.x - x;
      const dy = b.y - y;
      if (dx * dx + dy * dy > r2) return;
      b.destroy();
    });
  }

  /** timewarp —— 全场物理 timeScale 拉慢，玩家正常运行 */
  private _doTimewarp(): void {
    const fx = SKILL_FX.timewarp;
    this._timewarpUntil = performance.now() + fx.durationMs;
    this.scene.physics.world.timeScale = fx.worldTimeScale;
    // 视觉：屏角紫色 vignette + 一圈紫色波纹从屏中扩散
    const vignette = this.scene.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT)
      .setStrokeStyle(36, 0xc084ff, 0.55)
      .setDepth(188)
      .setScrollFactor(0)
      .setAlpha(0);
    this.scene.tweens.add({
      targets: vignette,
      alpha: { from: 0, to: 1 },
      duration: 200,
      yoyo: false,
    });
    this.scene.time.delayedCall(fx.durationMs - 200, () => {
      this.scene.tweens.add({
        targets: vignette,
        alpha: 0,
        duration: 200,
        onComplete: () => vignette.destroy(),
      });
    });
    this.sound?.upgrade();
  }

  /** nuke ULT —— 清屏所有非 Boss 怪 + Boss 大量受伤 + 清掉 Boss 弹幕 */
  private _doNuke(): void {
    const fx = SKILL_FX.nuke;
    // 屏闪 + 强震
    const flash = this.scene.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xffffff, 0.85)
      .setDepth(200)
      .setScrollFactor(0);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 380,
      onComplete: () => flash.destroy(),
    });
    this.scene.cameras.main.shake(280, 0.020);
    this.effects.explosion(GAME_WIDTH / 2, GAME_HEIGHT / 2, { big: true, color: 0xffffff, count: 40 });

    const enemies = this.hooks.getEnemyGroup().getChildren().slice() as Enemy[];
    for (const en of enemies) {
      if (!en.active || en._dead) continue;
      if (en.isBoss) {
        // Boss 不秒 —— 扣比例 HP，触发标准 takeDamage 流程（含狂暴判定）
        const dmg = Math.max(1, Math.round(en.maxHp * fx.bossHpRatio));
        const dead = en.takeDamage(dmg);
        if (dead) {
          en._dead = true;
          this.hooks.onEnemyKilled(en);
        }
      } else {
        en._dead = true;
        this.hooks.onEnemyKilled(en);
      }
    }
    this.hooks.getBossBulletGroup().clear(true, true);
    this.sound?.bossKill();
    // 释放后给一个短"持续期"，避免连点（虽然 charge 已经 0 了）
    this.ult.activeUntil = performance.now() + 600;
  }

  /** frenzy ULT —— 5s 暴走：射速 ×2.5、弹数 +5、必暴击 */
  private _doFrenzy(): void {
    const fx = SKILL_FX.frenzy;
    const now = performance.now();
    this._frenzyUntil = now + fx.durationMs;
    this.ult.activeUntil = this._frenzyUntil;

    // 快照原值
    this._frenzySnapshot = {
      fireRate: this.player.shootingDelay,
      bulletCount: this.player.bulletCount,
      critChance: this.player.critChance,
    };
    this.player.shootingDelay = this.player.shootingDelay * fx.fireRateMul;
    this.player.bulletCount = this.player.bulletCount + fx.bulletCountAdd;
    if (fx.forceCrit) this.player.critChance = 1;

    // 视觉：玩家身上橙色光环 + 屏角橙色 vignette
    const ring = this.scene.add
      .circle(this.player.x, this.player.y, 50, 0xff8a3c, 0)
      .setStrokeStyle(3, 0xff8a3c, 0.85)
      .setDepth(48);
    this.scene.tweens.add({
      targets: ring,
      scale: { from: 0.6, to: 1.4 },
      alpha: { from: 1, to: 0 },
      duration: 600,
      repeat: Math.floor(fx.durationMs / 600) - 1,
      onComplete: () => ring.destroy(),
    });
    // ring 跟随玩家：每帧贴上去（用 Phaser update 钩子简化做 —— 直接绑定到 scene.events.on('postupdate')）
    const follow = () => {
      if (!ring.active) return;
      ring.x = this.player.x;
      ring.y = this.player.y;
    };
    this.scene.events.on('postupdate', follow);
    this.scene.time.delayedCall(fx.durationMs, () => {
      this.scene.events.off('postupdate', follow);
    });

    this.sound?.upgrade();
    this.scene.cameras.main.shake(160, 0.010);
  }

  // ─── 内部 ─────────────────────────────────────────────────────

  private _ultCfg() { return ULT_SKILLS[this.ult.key]; }
}
